import { api, ApiError, isAbortError } from "./api";

const RETRY_DELAYS = [500, 1000];

function waitForRetry(delay: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    signal.throwIfAborted();
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delay);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

// Retry only failed reads, never writes or permanent authentication/validation errors.
export async function retryDashboardRead<T>(read: () => Promise<T>, signal: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    signal.throwIfAborted();
    try {
      return await read();
    } catch (error) {
      const transient = error instanceof TypeError ||
        (error instanceof ApiError && [408, 429, 500, 502, 503, 504].includes(error.status));
      if (signal.aborted || isAbortError(error) || !transient || attempt >= RETRY_DELAYS.length) {
        throw error;
      }
      await waitForRetry(RETRY_DELAYS[attempt], signal);
    }
  }
}

export async function loadDashboardData(
  params: { start?: string; end?: string },
  comparison: { start?: string; end?: string } | null,
  parentSignal?: AbortSignal,
) {
  const controller = new AbortController();
  const signal = controller.signal;
  const onAbort = () => controller.abort();
  if (parentSignal?.aborted) controller.abort();
  parentSignal?.addEventListener("abort", onAbort, { once: true });
  const read = <T>(request: () => Promise<T>) => retryDashboardRead(request, signal);
  const falhas: string[] = [];
  const optional = async <T>(name: string, request: () => Promise<T>, fallback: T): Promise<T> => {
    try {
      return await read(request);
    } catch (error) {
      signal.throwIfAborted();
      if (isAbortError(error)) throw error;
      falhas.push(name);
      console.error(`Dashboard: ${name} indisponivel`, error);
      return fallback;
    }
  };

  try {
    const [vendas, despesas, produtos, insumos, vendasComparativo, despesasComparativo] = await Promise.all([
      read(() => api.listarVendas(params, { signal })),
      optional("despesas", () => api.listarDespesasPorPeriodo(params, { signal }), null),
      optional("produtos", () => api.listarProdutos(undefined, { signal }), []),
      optional("insumos", () => api.listarInsumos({ signal }), []),
      comparison ? optional("vendasComparativo", () => api.listarVendas(comparison, { signal }), []) : Promise.resolve([]),
      comparison ? optional("despesasComparativo", () => api.listarDespesasPorPeriodo(comparison, { signal }), null) : Promise.resolve(null),
    ]);
    const soldIds = new Set(vendas.flatMap((venda) => (venda.itens ?? []).map((item) => Number(item.produto_id))));
    const compositionEntries: [number, Awaited<ReturnType<typeof api.getComposicao>>][] = [];
    const queue = [...new Map(produtos.filter((p) => p.possui_composicao && soldIds.has(p.id)).map((p) => [p.id, p])).values()];
    // A failed recipe must not cancel independent indicators or trigger retry storms.
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
      while (queue.length) {
        signal.throwIfAborted();
        const produto = queue.shift()!;
        try {
          compositionEntries.push([produto.id, await api.getComposicao(produto.id, { signal })]);
        } catch (error) {
          signal.throwIfAborted();
          if (isAbortError(error)) throw error;
          falhas.push(`composicao:${produto.id}`);
          console.error(`Dashboard: composicao ${produto.id} indisponivel`, error);
        }
      }
    }));
    signal.throwIfAborted();
    return {
      vendas, despesas: despesas?.despesas ?? [], produtos, insumos, falhas,
      vendasComparativo, despesasComparativo: despesasComparativo?.despesas ?? [],
      composicaoPorProduto: Object.fromEntries(compositionEntries),
    };
  } finally {
    parentSignal?.removeEventListener("abort", onAbort);
    controller.abort();
  }
}
